import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../lib/session';
import {
  useAuthReport, useConfig, useConfigMutation, useConsoleUsers, useDisputes,
  useDriver, useDriverMutations, useDrivers, useChargePointMutations, useChargePoints,
  useUserMutations,
} from '../lib/queries';
import { ROLE_LABEL, ROLE_NAV } from '../data/types';
import type { Role, ChargePoint } from '../data/types';
import { BFF, live } from '../data/live';
import type { AuthReport, ConfigEntry, MailLogEntry } from '../data/live';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/* ------------------------------------------------------ users and roles --- */

/* Every screen key the server's ROLE_SCREENS knows, in nav order. A key
   missing here is a row missing from the matrix — which is how charge points,
   connectors, the test bench and top-ups went unlisted. */
const SCREENS: Array<{ key: string; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' }, { key: 'stations', label: 'Stations' },
  { key: 'chargepoints', label: 'Charge points' }, { key: 'connectors', label: 'Connectors' },
  { key: 'testbench', label: 'Test bench' }, { key: 'ocpp', label: 'OCPP messages' },
  { key: 'drivers', label: 'Drivers' }, { key: 'agents', label: 'Agents' },
  { key: 'transactions', label: 'Transactions' }, { key: 'topups', label: 'Top-ups' },
  { key: 'tariffs', label: 'Tariffs' }, { key: 'disputes', label: 'Disputes' },
  { key: 'reports', label: 'Reports' }, { key: 'users', label: 'Users & roles' },
  { key: 'releases', label: 'App releases' }, { key: 'maps', label: 'Maps & keys' },
  { key: 'activity', label: 'Activity log' },
];

const ROLES: Role[] = ['superadmin', 'operator', 'support'];

export function Users() {
  const { user } = useSession();
  const { data, isLoading, error } = useConsoleUsers();
  const { create, update } = useUserMutations();
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ email: string; temporaryPassword: string } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (isLoading) return <Loading />;
  if (error || !data) {
    return <Empty title="Console accounts are not available." hint={loadFailedHint(error)} />;
  }

  const act = (fn: () => void) => { setFailed(null); fn(); };

  return (
    <>
      <div className="tiles">
        <Tile k="Accounts" v={data.length} d="can sign in" />
        <Tile k="Active" v={data.filter((u) => u.status === 'active').length} d="not suspended" />
        <Tile
          k="Awaiting first sign-in"
          v={data.filter((u) => u.mustChangePassword).length}
          d="still on a generated password"
        />
        <Tile k="You are" v={user ? ROLE_LABEL[user.role] : '—'} d={user?.email ?? ''} />
      </div>

      {issued && (
        <div className="card" style={{ marginTop: 14, borderColor: 'var(--primary)' }}>
          <div className="li-title">Temporary password for {issued.email}</div>
          <div className="mono" style={{ fontSize: 16, margin: '8px 0', userSelect: 'all' }}>
            {issued.temporaryPassword}
          </div>
          <div className="li-sub">
            Shown once. It is stored only as a hash and cannot be retrieved — if
            it is lost, issue a new one. Give it to them over something other
            than the channel they will use to sign in, and they must change it
            before the console will let them do anything else.
          </div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => setIssued(null)}>
            I have passed it on
          </button>
        </div>
      )}

      {failed && <div className="note" style={{ marginTop: 12, color: 'var(--bad)' }}>{failed}</div>}

      <div className="toolbar">
        <div className="li-sub">
          Accounts are created here. There is no self sign-up.
        </div>
        <button className="btn sm" onClick={() => setAdding(true)}>Add account</button>
      </div>

      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>Account</th><th>Role</th><th>Last signed in</th>
              <th className="num">Sessions</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id} className={u.status === 'suspended' ? 'off' : undefined}>
                <td>
                  <div className="li-title">{u.name}</div>
                  <div className="li-sub mono">{u.email}</div>
                </td>
                <td>
                  <select
                    className="field" style={{ width: 'auto', padding: '2px 6px' }}
                    value={u.role}
                    // You cannot change your own role: the server refuses it
                    // anyway, and offering it invites the support call.
                    disabled={u.email === user?.email}
                    onChange={(e) => act(() => update.mutate(
                      { id: u.id, role: e.target.value },
                      { onError: (err: Error) => setFailed(errorText(err)) },
                    ))}
                  >
                    {(['superadmin', 'operator', 'support'] as const).map((r) => (
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                    ))}
                  </select>
                </td>
                <td className="li-sub mono">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'never'}
                </td>
                <td className="num mono">{u.activeSessions}</td>
                <td>
                  <Pill tone={u.status === 'active' ? (u.mustChangePassword ? 'mid' : 'on') : 'off'}>
                    {u.status === 'suspended' ? 'Suspended'
                      : u.mustChangePassword ? 'Must set password' : 'Active'}
                  </Pill>
                </td>
                <td className="act">
                  <button
                    className="btn sm ghost"
                    disabled={update.isPending || u.email === user?.email}
                    onClick={() => act(() => update.mutate(
                      { id: u.id, status: u.status === 'active' ? 'suspended' : 'active' },
                      { onError: (err: Error) => setFailed(errorText(err)) },
                    ))}
                  >
                    {u.status === 'active' ? 'Suspend' : 'Restore'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="li-title">What each role can reach</div>
        <div className="li-sub" style={{ marginBottom: 8 }}>
          The console hides what a role cannot use, and the routes are guarded —
          but both are conveniences. Every endpoint checks this again on the
          server, because a hidden menu item is not a permission check.
        </div>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Screen</th>
                {ROLES.map((r) => <th key={r} className="num">{ROLE_LABEL[r]}</th>)}
              </tr>
            </thead>
            <tbody>
              {SCREENS.map((sc) => (
                <tr key={sc.key}>
                  <td>{sc.label}</td>
                  {ROLES.map((r) => (
                    <td key={r} className="num">
                      {ROLE_NAV[r].includes(sc.key)
                        ? <span style={{ color: 'var(--ok)', fontWeight: 700 }}>✓</span>
                        : <span className="faint">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {adding && (
        <AddUser
          pending={create.isPending}
          onClose={() => setAdding(false)}
          onSubmit={(v) => act(() => create.mutate(v, {
            onSuccess: (r) => { setIssued(r); setAdding(false); },
            onError: (err: Error) => setFailed(errorText(err)),
          }))}
        />
      )}
    </>
  );
}

function AddUser({ pending, onClose, onSubmit }: {
  pending: boolean;
  onClose: () => void;
  onSubmit: (v: { email: string; name: string; role: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('support');

  const bad = !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? 'Enter a valid email address.'
    : name.trim().length < 2 ? 'Name is too short.' : null;

  return (
    <div
      role="dialog" aria-label="Add console account"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 400 }}>
        <div className="li-title" style={{ fontSize: 15 }}>Add a console account</div>
        <div className="li-sub" style={{ marginBottom: 10 }}>
          A password is generated for them and shown to you once. They cannot use
          the console for anything until they have replaced it.
        </div>

        <label className="li-sub" htmlFor="u-email">Email</label>
        <input id="u-email" className="field" type="email" value={email}
               onChange={(e) => setEmail(e.target.value)} style={{ marginBottom: 8 }} />

        <label className="li-sub" htmlFor="u-name">Name</label>
        <input id="u-name" className="field" value={name}
               onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8 }} />

        <label className="li-sub" htmlFor="u-role">Role</label>
        <select id="u-role" className="field" value={role} onChange={(e) => setRole(e.target.value)}>
          {(['support', 'operator', 'superadmin'] as const).map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
          ))}
        </select>

        {bad && <div className="li-sub" style={{ color: 'var(--bad)', marginTop: 8 }}>{bad}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                  onClick={() => onSubmit({ email: email.trim().toLowerCase(), name: name.trim(), role })}>
            Create
          </button>
          <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ maps & keys -- */

/* Minor units <-> whole currency, by string arithmetic: "20.5" is 2050 and 2050
   is "20.50", with no float in between to turn 0.1 + 0.2 into a different bill.
   Anything that is not a number of at most two decimals is passed through as
   typed, and the server refuses it with a readable error. */
function toMinor(major: string): string {
  const m = /^\s*(\d+)(?:\.(\d{0,2}))?\s*$/.exec(major);
  if (!m) return major;
  return String(Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || '0'));
}
function toMajor(minor: string): string {
  if (!/^\d+$/.test(minor)) return minor;
  const n = Number(minor);
  return `${Math.floor(n / 100)}.${String(n % 100).padStart(2, '0')}`;
}

export function Maps() {
  const { data, isLoading, error: loadError } = useConfig();
  const save = useConfigMutation();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [reveal, setReveal] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const cfg = useMemo(
    () => Object.fromEntries((data ?? []).map((c) => [c.key, c])),
    [data],
  );
  // A fresh server read clears the draft, so an untouched field is never
  // written back with a stale value.
  useEffect(() => {
    if (data) setDraft({});
  }, [data]);

  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!data) return <Empty title="Settings are not available." hint={loadFailedHint(loadError)} />;

  const val = (k: string) => draft[k] ?? cfg[k]?.value ?? '';
  const set = (k: string, v: string) => { setDraft((d) => ({ ...d, [k]: v })); setMsg(null); };

  const submit = () => {
    const changed = Object.fromEntries(
      Object.entries(draft).filter(([k, v]) => v !== (cfg[k]?.value ?? '')),
    );
    if (!Object.keys(changed).length) { setMsg('Nothing changed.'); return; }
    save.mutate(changed, {
      onSuccess: () => { setDraft({}); setMsg('Saved.'); },
      onError: (e: Error) => setMsg(errorText(e)),
    });
  };

  const Row = ({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', gap: 16, padding: '11px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
      {/* Fixed, not min-width: a long hint would otherwise stretch this column
          and knock that one row's input out of line with the rest. */}
      <div style={{ flex: '0 0 210px' }}>
        <div className="li-title">{label}</div>
        <div className="li-sub">{hint}</div>
      </div>
      <div style={{ flex: 1, minWidth: 240, maxWidth: 420 }}>{children}</div>
    </div>
  );

  /* A secret never comes back from the server, so "Set" is all the console
     could say about one — and "Set" is exactly as true of the right key as of
     a pasted-in typo. The API sends back the last six characters, which is
     enough to check a key against the one in the Cloud console and useless to
     anyone who intercepts it. */
  const keyTile = (c?: ConfigEntry) =>
    c?.isSet ? (c.tail ? `…${c.tail}` : 'Set') : 'Not set';
  const keyPlaceholder = (c?: ConfigEntry) =>
    c?.isSet
      ? `set${c.tail ? ` · …${c.tail}` : ''} — type to replace`
      : 'not set';

  const tiles = val('map.tiles');
  const routing = val('map.routing');
  const mailOn = val('mail.enabled') === 'true';
  const otpOn = val('auth.console_otp') === 'true';
  /* Only the *unsaved* choice triggers the warning. Reading the saved value
     would mean shouting at someone who has just turned the thing off. */
  const otpDraftOn = draft['auth.console_otp'] === 'true';

  return (
    <>
      <div className="tiles">
        <Tile k="Map tiles" v={tiles === 'osm' ? 'OSM' : 'Google'} d={tiles === 'osm' ? 'MapTiler' : 'Maps SDK'} />
        <Tile k="Routing" v={routing === 'osrm' ? 'OSRM' : 'Google'} d={routing === 'osrm' ? 'self-hostable' : 'Directions API'} />
        <Tile k="MapTiler key" v={keyTile(cfg['map.maptiler_key'])} d={tiles === 'osm' ? 'in use by the app' : 'stored, not in use'} />
        <Tile k="Google key" v={keyTile(cfg['map.google_key'])} d="served to the app" />
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <Row label="Map tiles" hint="Which basemap the driver app draws.">
          <div className="chips">
            <Chip label="OSM · MapTiler" selected={tiles === 'osm'} onClick={() => set('map.tiles', 'osm')} />
            <Chip label="Google Maps" selected={tiles === 'google'} onClick={() => set('map.tiles', 'google')} />
          </div>
        </Row>

        <Row label="MapTiler key" hint={cfg['map.maptiler_key']?.hint ?? ''}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="field" type={reveal ? 'text' : 'password'}
              value={draft['map.maptiler_key'] ?? ''}
              placeholder={keyPlaceholder(cfg['map.maptiler_key'])}
              onChange={(e) => set('map.maptiler_key', e.target.value)}
            />
            <button className="btn ghost sm" onClick={() => setReveal(!reveal)}>
              {reveal ? 'Hide' : 'Show'}
            </button>
          </div>
        </Row>

        {/* This is the key the driver app FETCHES: it reaches the phone through
            GET /v1/app/config, cached on the device, refreshed at launch. It
            covers the web-service calls the app makes with a key in hand —
            Directions above all. It is NOT the Maps SDK for Android key, which
            the SDK reads out of the app's own manifest at init and which no
            amount of config can replace; both keys may be the same string, but
            changing it here only moves the first one. */}
        <Row label="Google Maps key" hint={cfg['map.google_key']?.hint ?? ''}>
          <input
            className="field" type={reveal ? 'text' : 'password'}
            value={draft['map.google_key'] ?? ''}
            placeholder={keyPlaceholder(cfg['map.google_key'])}
            onChange={(e) => set('map.google_key', e.target.value)}
          />
        </Row>

        {/* A SECOND Google key, and deliberately so. The one above is restricted
            to the Android package and signing certificate, which the Directions
            web service refuses; this one is restricted to the BFF's IP address
            and never leaves it. */}
        <Row label="Google Directions key" hint={cfg['map.google_directions_key']?.hint ?? ''}>
          <input
            className="field" type={reveal ? 'text' : 'password'} autoComplete="off"
            value={draft['map.google_directions_key'] ?? ''}
            placeholder={keyPlaceholder(cfg['map.google_directions_key'])}
            onChange={(e) => set('map.google_directions_key', e.target.value.trim())}
          />
        </Row>

        <Row label="Routing" hint="Tiles and routing are separate services; choosing one does not choose the other.">
          <div className="chips">
            <Chip label="OSRM" selected={routing === 'osrm'} onClick={() => set('map.routing', 'osrm')} />
            <Chip label="Google Directions" selected={routing === 'google'} onClick={() => set('map.routing', 'google')} />
          </div>
        </Row>

        <Row label="OSRM endpoint" hint={cfg['map.osrm_url']?.hint ?? ''}>
          <input className="field mono" value={val('map.osrm_url')}
                 onChange={(e) => set('map.osrm_url', e.target.value)} />
        </Row>

        <Row label="Default currency" hint="Used where a charge point has no currency of its own.">
          <input className="field mono" value={val('money.currency')} maxLength={3}
                 onChange={(e) => set('money.currency', e.target.value.toUpperCase())} />
        </Row>
      </div>

      {/* Prepaid protection (spark_ev_bff/src/lib/charge_rules.ts). Stored in
          minor units like every amount; typed and shown in whole currency here,
          converted exactly — never through a float. */}
      <h3 style={{ margin: '22px 0 0', fontSize: 15 }}>Charging rules</h3>
      <div className="li-sub" style={{ marginBottom: 8 }}>
        Checked before a charger is told to start. Every charge reserves what it may spend;
        the server stops it at the driver's limit.
      </div>
      <div className="card">
        <Row label="Minimum balance to start" hint={`In ${val('money.currency') || 'ETB'}. Less available than this is refused before the charger is contacted.`}>
          <input className="field mono" inputMode="decimal"
                 value={toMajor(val('charge.min_start_balance'))}
                 onChange={(e) => set('charge.min_start_balance', toMinor(e.target.value))} />
        </Row>
        <Row label="Largest hold for a Full charge" hint={`In ${val('money.currency') || 'ETB'}. A Full charge reserves what the balance buys, up to this — so a second car can still start.`}>
          <input className="field mono" inputMode="decimal"
                 value={toMajor(val('charge.full_hold_max'))}
                 onChange={(e) => set('charge.full_hold_max', toMinor(e.target.value))} />
        </Row>
        <Row label="Stop margin (seconds)" hint={cfg['charge.stop_margin_seconds']?.hint ?? 'How far ahead of a limit the server stops a charge. 5–120.'}>
          <input className="field mono" inputMode="numeric"
                 value={val('charge.stop_margin_seconds')}
                 onChange={(e) => set('charge.stop_margin_seconds', e.target.value.replace(/\D/g, ''))} />
        </Row>
      </div>

      {routing === 'google' && !cfg['map.google_directions_key']?.isSet && (
        <div className="note" style={{ marginTop: 10 }}>
          <b>Google routing has no key.</b> The app asks the BFF for routes and the
          BFF has nothing to ask Google with, so every route quietly falls back to
          OSRM. Set a server key above, or set routing to OSRM and mean it.
        </div>
      )}

      <h3 style={{ margin: '22px 0 0', fontSize: 15 }}>Email</h3>
      <div className="li-sub" style={{ marginBottom: 8 }}>
        Sign-in codes and password resets are sent from here. Nothing else in the
        platform sends email.
      </div>

      <div className="tiles">
        <Tile k="Sending" v={mailOn ? 'On' : 'Off'}
              d={mailOn ? 'codes are delivered' : 'codes are generated but not sent'} />
        <Tile k="SMTP host" v={val('mail.host') || 'Not set'} d={`port ${val('mail.port') || '—'}`} />
        <Tile k="SMTP password" v={keyTile(cfg['mail.password'])} d="never sent to a browser" />
        <Tile k="Console codes" v={otpOn ? 'Required' : 'Off'}
              d={otpOn ? 'a code at every sign-in' : 'password only'} />
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <Row label="Sending" hint={cfg['mail.enabled']?.hint ?? ''}>
          <div className="chips">
            <Chip label="On" selected={mailOn} onClick={() => set('mail.enabled', 'true')} />
            <Chip label="Off" selected={!mailOn} onClick={() => set('mail.enabled', 'false')} />
          </div>
        </Row>

        <Row label="SMTP host" hint={cfg['mail.host']?.hint ?? ''}>
          <input className="field mono" value={val('mail.host')} placeholder="smtp.gmail.com"
                 onChange={(e) => set('mail.host', e.target.value.trim())} />
        </Row>

        <Row label="Port" hint={cfg['mail.port']?.hint ?? ''}>
          <input className="field mono" value={val('mail.port')} inputMode="numeric"
                 style={{ maxWidth: 120 }}
                 onChange={(e) => set('mail.port', e.target.value.replace(/\D/g, ''))} />
        </Row>

        <Row label="Security" hint={cfg['mail.security']?.hint ?? ''}>
          <div className="chips">
            <Chip label="STARTTLS" selected={val('mail.security') === 'starttls'}
                  onClick={() => set('mail.security', 'starttls')} />
            <Chip label="TLS" selected={val('mail.security') === 'tls'}
                  onClick={() => set('mail.security', 'tls')} />
            <Chip label="None" selected={val('mail.security') === 'none'}
                  onClick={() => set('mail.security', 'none')} />
          </div>
        </Row>

        <Row label="Username" hint={cfg['mail.username']?.hint ?? ''}>
          <input className="field mono" value={val('mail.username')} autoComplete="off"
                 onChange={(e) => set('mail.username', e.target.value.trim())} />
        </Row>

        <Row label="Password" hint={cfg['mail.password']?.hint ?? ''}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="field" type={reveal ? 'text' : 'password'} autoComplete="new-password"
              value={draft['mail.password'] ?? ''}
              placeholder={keyPlaceholder(cfg['mail.password'])}
              onChange={(e) => set('mail.password', e.target.value)}
            />
            <button className="btn ghost sm" onClick={() => setReveal(!reveal)}>
              {reveal ? 'Hide' : 'Show'}
            </button>
          </div>
        </Row>

        <Row label="From address" hint={cfg['mail.from_address']?.hint ?? ''}>
          <input className="field mono" value={val('mail.from_address')}
                 onChange={(e) => set('mail.from_address', e.target.value.trim())} />
        </Row>

        <Row label="From name" hint={cfg['mail.from_name']?.hint ?? ''}>
          <input className="field" value={val('mail.from_name')}
                 onChange={(e) => set('mail.from_name', e.target.value)} />
        </Row>

        <Row label="Reply-to" hint={cfg['mail.reply_to']?.hint ?? ''}>
          <input className="field mono" value={val('mail.reply_to')} placeholder="optional"
                 onChange={(e) => set('mail.reply_to', e.target.value.trim())} />
        </Row>

        <Row label="Console sign-in code" hint={cfg['auth.console_otp']?.hint ?? ''}>
          <div className="chips">
            <Chip label="Password only" selected={!otpOn}
                  onClick={() => set('auth.console_otp', 'false')} />
            <Chip label="Password + emailed code" selected={otpOn}
                  onClick={() => set('auth.console_otp', 'true')} />
          </div>
        </Row>
      </div>

      {otpDraftOn && !mailOn && (
        <div className="note" style={{ marginTop: 10 }}>
          <b>Sending is off.</b> Requiring a code while no email can leave the
          server locks every operator out of this console, including you. Turn
          sending on and send yourself a test first.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
        <button className="btn sm" onClick={submit} disabled={save.isPending}>Save settings</button>
        {msg && <span className="li-sub">{msg}</span>}
        {cfg['map.tiles']?.updatedBy && (
          <span className="li-sub faint">last changed by {cfg['map.tiles'].updatedBy}</span>
        )}
      </div>

      <div className="note" style={{ marginTop: 14 }}>
        <b>Keys and passwords are write-only here.</b> The console can set one and
        can see that one is set; it can never read it back. A secret served to
        every browser that opens this page is a secret that leaks the first time
        someone opens devtools on a shared screen. The driver app reads the map
        keys from the BFF at launch; the SMTP password never leaves the server.
      </div>

      <MailCheck />
    </>
  );
}

/**
 * Prove email works, and show what happened when it did not.
 *
 * Two separate buttons because they fail for different reasons. A connection
 * check that passes and a send that fails means the settings are right and the
 * message was rejected — a different problem, with a different fix, from a
 * password the provider will not accept.
 */
function MailCheck() {
  const { user } = useSession();
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState<null | 'verify' | 'send'>(null);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);

  const log = useQuery({
    queryKey: ['mail-log'],
    queryFn: () => live.mailLog(20),
    // Only while someone is looking at this screen.
    refetchInterval: 20_000,
  });

  const run = async (which: 'verify' | 'send') => {
    setBusy(which);
    setSaid(null);
    try {
      const r = which === 'verify' ? await live.mailVerify() : await live.mailTest(to.trim() || undefined);
      setSaid(r.ok
        ? {
            ok: true,
            text: which === 'verify'
              ? 'The mail server accepted these settings.'
              : `Sent to ${to.trim() || (user?.email ?? 'you')}. If it does not arrive, look in spam before changing anything.`,
          }
        : { ok: false, text: explainMail(r.error) });
    } catch (e) {
      setSaid({ ok: false, text: errorText(e) });
    } finally {
      setBusy(null);
      void log.refetch();
    }
  };

  const rows = log.data?.items ?? [];

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="li-title">Check email</div>
      <div className="li-sub" style={{ marginBottom: 10 }}>
        Do this after every change, and before turning console sign-in codes on.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="field mono" style={{ maxWidth: 280 }} value={to}
               placeholder={user?.email ?? 'your address'}
               onChange={(e) => setTo(e.target.value.trim())} />
        <button className="btn ghost sm" disabled={busy !== null} onClick={() => void run('verify')}>
          {busy === 'verify' ? 'Checking…' : 'Check connection'}
        </button>
        <button className="btn sm" disabled={busy !== null} onClick={() => void run('send')}>
          {busy === 'send' ? 'Sending…' : 'Send test email'}
        </button>
      </div>

      {said && (
        <div className="li-sub" role="status"
             style={{ marginTop: 10, color: said.ok ? 'var(--good)' : 'var(--bad)' }}>
          {said.text}
        </div>
      )}

      <div className="li-title" style={{ marginTop: 16 }}>Recent deliveries</div>
      {rows.length === 0 ? (
        <div className="li-sub">Nothing sent yet.</div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 6 }}>
          <table className="tbl">
            <thead>
              <tr><th>When</th><th>To</th><th>Purpose</th><th>Result</th></tr>
            </thead>
            <tbody>
              {rows.map((m: MailLogEntry) => (
                <tr key={m.id}>
                  <td className="mono nowrap">{new Date(m.at).toLocaleString()}</td>
                  <td className="mono">{m.to}</td>
                  <td>{m.purpose}</td>
                  <td>
                    {m.ok
                      ? <Pill tone="on">sent{m.ms ? ` · ${m.ms} ms` : ''}</Pill>
                      : <><Pill tone="off">failed</Pill>{' '}
                          <span className="li-sub">{explainMail(m.error ?? undefined)}</span></>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note" style={{ marginTop: 12 }}>
        <b>The code itself is never stored.</b> This log records that a message
        was attempted and what came back, not what was in it — a log that keeps
        sign-in codes in clear is a second copy of everyone&rsquo;s credentials.
      </div>
    </div>
  );
}

/** Provider refusals are written for machines. These are the ones worth
 *  translating, because each points at a different person to go and talk to. */
function explainMail(error?: string): string {
  if (!error) return 'Failed.';
  const e = error.toLowerCase();
  if (e === 'mail_disabled') return 'Sending is switched off above.';
  if (e === 'mail_host_missing') return 'No SMTP host is set.';
  if (e === 'mail_from_missing') return 'No from-address is set.';
  if (e === 'mail_username_missing') return 'A password is set but no username.';
  if (e.includes('username and password not accepted') || e.includes('invalid login')
      || e.includes('535')) {
    return 'The provider rejected the username or password. For Gmail this must '
      + 'be an app password, and it stops working the moment it is revoked.';
  }
  if (e.includes('daily user sending limit') || e.includes('quota')) {
    return 'The mailbox has hit its sending limit for the day.';
  }
  if (e.includes('etimedout') || e.includes('econnrefused') || e.includes('timeout')) {
    return 'Could not reach the mail server — check the host, the port, and that '
      + 'the server is allowed to make outbound connections on it.';
  }
  if (e.includes('self signed') || e.includes('certificate')) {
    return 'The mail server presented a certificate that could not be verified.';
  }
  return error;
}

/* ------------------------------------------------------------- diagnostics */

export function DataSource() {
  const { error } = useChargePoints();
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => live.health(),
    refetchInterval: 30_000,
    retry: false,
  });
  const ok = !error && !!health?.ok;

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="row">
        <div>
          <div className="li-title">Data source</div>
          <div className="li-sub mono">{BFF}</div>
        </div>
        <Pill tone={ok ? 'on' : 'off'}>{ok ? 'live' : 'unreachable'}</Pill>
      </div>
      <div className="li-sub" style={{ marginTop: 8 }}>
        Charge points, connectors and sessions come from CitrineOS; agents, the
        audit trail, releases and operator overrides from the platform database.
        There is no sample mode — if a server cannot be reached the screen says
        so rather than showing plausible numbers as if they were current.
        {/* /health does not report a count, and printing the field blind
            showed "undefined audit entries". Only said when the server says. */}
        {typeof health?.entries === 'number' && <> · {health.entries} audit entries recorded.</>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- drivers */

const minor = (v: string | number | null | undefined) => (Number(v ?? 0) / 100).toFixed(2);

export function Drivers() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ name: string; temporaryPassword: string } | null>(null);
  /* The register dialog's refusal — a duplicate phone or email above all,
     which used to leave the dialog open and silent. */
  const [addError, setAddError] = useState<string | null>(null);
  const { data, isLoading, error } = useDrivers(q);
  const { data: report } = useAuthReport();
  const { create } = useDriverMutations();

  if (isLoading) return <Loading />;
  if (error || !data) {
    return <Empty title="Driver records are not available." hint={loadFailedHint(error)} />;
  }

  const verified = data.filter((d) => d.docStatus === 'verified').length;
  const balance = data.reduce((n, d) => n + Number(d.balanceMinor ?? 0), 0);
  const live = data.filter((d) => !d.closedAt);
  const onCurrentTerms = live.filter((d) => d.termsVersion != null && d.termsVersion === d.termsCurrent).length;

  return (
    <>
      <div className="tiles">
        <Tile k="Drivers" v={data.length} d="registered" />
        <Tile k="Verified" v={verified} d="documents accepted" />
        <Tile k="Suspended" v={data.filter((d) => d.status === 'suspended').length} d="cannot charge" />
        <Tile k="Agreed to current Terms" v={`${onCurrentTerms} / ${live.length}`}
              d={`${data.length - live.length} deleted their account`} />
        <Tile k="Wallet float" v={minor(balance)} d="held across all wallets" />
      </div>

      <AuthHealth report={report} />

      {issued && (
        <div className="card" style={{ marginTop: 12, borderColor: 'var(--primary)' }}>
          <div className="li-title">Temporary password for {issued.name}</div>
          <div className="mono" style={{ fontSize: 16, margin: '8px 0', userSelect: 'all' }}>
            {issued.temporaryPassword}
          </div>
          <div className="li-sub">
            Shown once, stored only as a hash. It is how they first sign in to the
            driver app.
          </div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => setIssued(null)}>
            I have passed it on
          </button>
        </div>
      )}

      <div className="toolbar">
        <button className="btn sm" onClick={() => { setAddError(null); setAdding(true); }}>Register driver</button>
        <input className="field" style={{ width: 'auto', minWidth: 220 }} value={q}
               placeholder="Search name, email or phone" onChange={(e) => setQ(e.target.value)}
               aria-label="Search drivers" />
      </div>

      {!data.length ? (
        <Empty
          title={q ? 'No drivers match that search.' : 'No drivers registered yet.'}
          hint={q ? undefined
            : 'Register one above. Adding a vehicle issues its idTag and makes the charger accept it — the two are one operation.'}
        />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Driver</th><th>Contact</th><th>Documents</th>
                <th className="num">Vehicles</th><th className="num">Sessions</th>
                <th className="num">Balance</th><th>Terms</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.id}>
                  <td className="li-title">{d.name}</td>
                  <td className="li-sub">
                    {d.email ?? d.phone}
                    {d.phone && d.email && <div>{d.phone}</div>}
                  </td>
                  <td>
                    <Pill tone={d.docStatus === 'verified' ? 'on' : d.docStatus === 'rejected' ? 'off' : 'mid'}>
                      {d.docStatus}
                    </Pill>
                  </td>
                  <td className="num mono">{d.vehicles}</td>
                  <td className="num mono">{d.sessions}</td>
                  <td className="num mono">{minor(d.balanceMinor)}</td>
                  <td>
                    {d.closedAt ? <span className="li-sub">—</span>
                      : d.termsVersion == null ? <Pill tone="mid">not agreed</Pill>
                      : (
                        <>
                          <Pill tone={d.termsVersion === d.termsCurrent ? 'on' : 'mid'}>
                            v{d.termsVersion}{d.termsVersion !== d.termsCurrent ? ' (old)' : ''}
                          </Pill>
                          {d.termsAcceptedAt && (
                            <div className="li-sub">{new Date(d.termsAcceptedAt).toLocaleDateString()}</div>
                          )}
                        </>
                      )}
                  </td>
                  <td>
                    {d.closedAt ? (
                      <>
                        <Pill tone="off">Deleted</Pill>
                        <div className="li-sub">{new Date(d.closedAt).toLocaleDateString()}</div>
                      </>
                    ) : <Pill tone={d.status === 'active' ? 'on' : 'off'}>{d.status}</Pill>}
                  </td>
                  <td className="act">
                    <button className="btn sm ghost" onClick={() => setOpen(d.id)}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <DriverPanel id={open} onClose={() => setOpen(null)} />}
      {adding && (
        <AddDriver
          pending={create.isPending}
          error={addError}
          onClose={() => setAdding(false)}
          onSubmit={(v) => { setAddError(null); create.mutate(v, {
            onSuccess: (r) => { setIssued({ name: r.name, temporaryPassword: r.temporaryPassword }); setAdding(false); },
            onError: (e: Error) => setAddError(errorText(e)),
          }); }}
        />
      )}
    </>
  );
}

/**
 * Whether our idTags and the CSMS's Authorizations still agree.
 *
 * Four things go wrong and every one of them is silent until a driver plugs in,
 * which is why this sits at the top of the screen rather than behind a button
 * nobody presses.
 */
function AuthHealth({ report }: { report: AuthReport | undefined }) {
  if (!report) return null;
  if (report.healthy) {
    return (
      <div className="card" style={{ marginTop: 12 }}>
        <div className="row">
          <div>
            <div className="li-title">Charger authorisations</div>
            <div className="li-sub">
              {report.checked} idTag{report.checked === 1 ? '' : 's'} checked against{' '}
              {report.csmsRows} row{report.csmsRows === 1 ? '' : 's'} in the CSMS
              {report.orphans.length > 0 && <> · {report.orphans.length} not issued by us</>}
            </div>
          </div>
          <Pill tone="on">in sync</Pill>
        </div>
      </div>
    );
  }
  return (
    <div className="card" style={{ marginTop: 12, borderColor: 'var(--bad)' }}>
      <div className="row">
        <div>
          <div className="li-title">Charger authorisations are out of sync</div>
          <div className="li-sub">
            Each of these is silent until someone plugs in.
          </div>
        </div>
        <Pill tone="off">{report.drift.length} to fix</Pill>
      </div>
      <div className="tbl" style={{ marginTop: 8 }}>
        <table>
          <thead><tr><th>Problem</th><th>idTag</th><th>Vehicle</th><th>What it means</th></tr></thead>
          <tbody>
            {report.drift.map((d) => (
              <tr key={d.kind + d.tag}>
                <td><span className="pill">{d.kind}</span></td>
                <td className="mono">{d.tag}</td>
                <td className="li-sub">{d.plate ?? '—'}</td>
                <td className="li-sub">{d.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** One driver: vehicles, their idTags, and what the charger will do with each. */
function DriverPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useDriver(id);
  /* The virtual idTag is still not editable. Cards are: they are physical,
     they get lost, and revoking one must not touch the app. */
  const { update, addVehicle, issueCard, revokeCard } = useDriverMutations();
  const [addingCard, setAddingCard] = useState<string | null>(null);
  const [losing, setLosing] = useState<{ id: string; tag: string } | null>(null);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const run = (fn: () => void) => { setFailed(null); fn(); };

  return (
    <div
      role="dialog" aria-label="Driver"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40, overflow: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'auto' }}>
        {isLoading || !data ? <Loading /> : (
          <>
            <div className="row">
              <div>
                <div className="li-title" style={{ fontSize: 15 }}>{data.driver.name}</div>
                <div className="li-sub mono">{data.driver.email ?? data.driver.phone}</div>
              </div>
              <Pill tone={data.driver.status === 'active' ? 'on' : 'off'}>{data.driver.status}</Pill>
            </div>

            {failed && <div className="note" style={{ marginTop: 10, color: 'var(--bad)' }}>{failed}</div>}

            <div className="chips" style={{ margin: '10px 0' }}>
              <button
                className="btn sm ghost"
                disabled={update.isPending}
                onClick={() => run(() => update.mutate({
                  id, status: data.driver.status === 'active' ? 'suspended' : 'active',
                }, { onError: (e: Error) => setFailed(errorText(e)) }))}
              >
                {data.driver.status === 'active' ? 'Suspend' : 'Restore'}
              </button>
              <button className="btn sm ghost" disabled={update.isPending}
                      onClick={() => run(() => update.mutate(
                        { id, docStatus: 'verified' },
                        { onError: (e: Error) => setFailed(errorText(e)) },
                      ))}>
                Mark documents verified
              </button>
              <button className="btn sm" onClick={() => setAddingVehicle(true)}>Add vehicle</button>
            </div>

            <div className="li-sub" style={{ marginBottom: 6 }}>
              Suspending blocks every tag at the charger. It does not delete them —
              the sessions this driver already ran still have to resolve to a real
              account.
            </div>

            {!data.vehicles.length ? (
              <Empty title="No vehicles." hint="Adding one issues its idTag and registers it with the CSMS." />
            ) : data.vehicles.map((v) => (
              <div key={v.id} className="card" style={{ marginTop: 10 }}>
                <div className="row">
                  <div>
                    <div className="li-title">{v.name}</div>
                    <div className="li-sub mono">{v.plate} · {v.connector}</div>
                  </div>
                </div>
                <div className="tbl" style={{ marginTop: 8 }}>
                  <table>
                    <thead>
                      <tr><th>idTag</th><th>Kind</th><th>Here</th><th>At the charger</th><th /></tr>
                    </thead>
                    <tbody>
                      {v.tags.map((t) => {
                        const csms = data.csms[t.tag];
                        // Green only when both sides agree AND the answer is yes.
                        const willCharge = csms?.status === 'Accepted' && t.status === 'active';
                        const disagrees = (t.status === 'active') !== (csms?.status === 'Accepted');
                        return (
                          <tr key={t.id}>
                            <td className="mono">{t.tag}</td>
                            <td className="li-sub">{t.kind}</td>
                            <td><Pill tone={t.status === 'active' ? 'on' : 'off'}>{t.status}</Pill></td>
                            <td>
                              <Pill tone={willCharge ? 'on' : disagrees ? 'mid' : 'off'}>
                                {csms?.status ?? 'no row'}
                              </Pill>
                              {disagrees && <div className="li-sub">these disagree</div>}
                            </td>
                            {/* Read-only. The tag is issued when the vehicle
                                is created and is not edited afterwards: past
                                sessions point at it and the CSMS bills against
                                it, so a tag that changes under a car is a car
                                whose history stops joining up. Editing it here
                                is also how BB-77777 ended up unable to charge
                                and AA-12345 ended up with two live tags. */}
                            <td className="act">
                              {/* Only a card can be acted on. The virtual tag
                                  is the vehicle's identity at the charger and
                                  stays read-only. */}
                              {t.kind === 'rfid' && t.status === 'active' ? (
                                <button
                                  className="btn sm ghost"
                                  disabled={revokeCard.isPending}
                                  title="For a card that has been lost. One-way: a card that can be switched back on is a card the finder can still use."
                                  onClick={() => setLosing({ id: t.id, tag: t.tag })}
                                >
                                  Report lost
                                </button>
                              ) : (
                                <span className="li-sub">
                                  {t.status === 'active' ? 'in service' : 'revoked'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="row" style={{ marginTop: 8, justifyContent: 'space-between' }}>
                  <div className="li-sub" style={{ maxWidth: 420 }}>
                    The vehicle's own idTag is issued when the vehicle is added and
                    never changes. RFID cards are extra, physical ways to start the
                    same car — add one per card, and report it lost if it goes
                    missing.
                  </div>
                  <button className="btn sm" onClick={() => setAddingCard(v.id)}>
                    Add a card
                  </button>
                </div>

                {/* A vehicle with no active tag cannot charge at all, and
                    deactivation is deliberately one-way — so this is the way
                    back, and it never resurrects the old credential. */}
                {!v.tags.some((t) => t.status === 'active') && (
                  <div className="note" style={{ marginTop: 8 }}>
                    <b>This vehicle has no idTag in service,</b> so it cannot start
                    a charge. The server issues one the next time the driver's app
                    loads their vehicles — there is nothing to do here.
                  </div>
                )}
              </div>
            ))}

            <button className="btn ghost" style={{ width: '100%', marginTop: 12 }} onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>

      {addingCard && (
        <AddCard
          pending={issueCard.isPending}
          onClose={() => setAddingCard(null)}
          onSubmit={(tag) => run(() => issueCard.mutate(
            { vehicleId: addingCard, tag },
            { onSuccess: () => setAddingCard(null), onError: (e: Error) => setFailed(errorText(e)) },
          ))}
        />
      )}

      {losing && (
        <ReportLost
          tag={losing.tag}
          pending={revokeCard.isPending}
          onClose={() => setLosing(null)}
          onConfirm={(reason) => run(() => revokeCard.mutate(
            { id: losing.id, reason },
            { onSuccess: () => setLosing(null), onError: (e: Error) => setFailed(errorText(e)) },
          ))}
        />
      )}

      {addingVehicle && data && (
        <AddVehicle
          pending={addVehicle.isPending}
          onClose={() => setAddingVehicle(false)}
          onSubmit={(v) => run(() => addVehicle.mutate({ driverId: id, ...v }, {
            onSuccess: () => setAddingVehicle(false),
            onError: (e: Error) => setFailed(errorText(e)),
          }))}
        />
      )}
    </div>
  );
}

function Sheet({ label, onClose, children }: {
  label: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div
      role="dialog" aria-label={label}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 50,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 400 }}>{children}</div>
    </div>
  );
}

function AddDriver({ pending, error, onClose, onSubmit }: {
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (v: { name: string; email?: string; phone?: string }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const bad = name.trim().length < 2 ? 'Name is too short.'
    : !email.trim() && !phone.trim() ? 'An email or a phone number is required — it is how they sign in.'
      : null;

  return (
    <Sheet label="Register driver" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>Register a driver</div>
      <div className="li-sub" style={{ marginBottom: 10 }}>
        A password is generated and shown once. Vehicles and idTags are added
        afterwards, from their record.
      </div>
      <label className="li-sub" htmlFor="d-name">Name</label>
      <input id="d-name" className="field" value={name} onChange={(e) => setName(e.target.value)}
             style={{ marginBottom: 8 }} />
      <label className="li-sub" htmlFor="d-phone">Phone</label>
      <input id="d-phone" className="field" value={phone} placeholder="+251…"
             onChange={(e) => setPhone(e.target.value)} style={{ marginBottom: 8 }} />
      <label className="li-sub" htmlFor="d-email">Email</label>
      <input id="d-email" className="field" type="email" value={email}
             onChange={(e) => setEmail(e.target.value)} />
      {(bad || error) && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
          {bad ?? error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onSubmit({
                  name: name.trim(),
                  ...(email.trim() ? { email: email.trim() } : {}),
                  ...(phone.trim() ? { phone: phone.trim() } : {}),
                })}>
          Register
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Sheet>
  );
}

const CONNECTORS = ['CCS2', 'Type 2', 'CHAdeMO', 'GB/T'];

function AddVehicle({ pending, onClose, onSubmit }: {
  pending: boolean;
  onClose: () => void;
  onSubmit: (v: { name: string; plate: string; connector: string }) => void;
}) {
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [connector, setConnector] = useState(CONNECTORS[0]!);
  const bad = !name.trim() ? 'Name the vehicle so the driver can tell two apart.'
    : !plate.trim() ? 'A plate is required.' : null;

  return (
    <Sheet label="Add vehicle" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>Add a vehicle</div>
      <div className="li-sub" style={{ marginBottom: 10 }}>
        This issues its idTag and registers it with the CSMS in one operation. If
        the CSMS write fails, the vehicle is not created — a vehicle that looks
        healthy here and is refused at the charger is the worst of both states.
      </div>
      <label className="li-sub" htmlFor="v-name">Name</label>
      <input id="v-name" className="field" value={name} placeholder="Hyundai Kona"
             onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8 }} />
      <label className="li-sub" htmlFor="v-plate">Plate</label>
      <input id="v-plate" className="field" value={plate} placeholder="AA-12345"
             onChange={(e) => setPlate(e.target.value)} style={{ marginBottom: 8 }} />
      <div className="li-sub">Connector</div>
      <div className="chips" style={{ marginTop: 4 }}>
        {CONNECTORS.map((cn) => (
          <Chip key={cn} label={cn} selected={connector === cn} onClick={() => setConnector(cn)} />
        ))}
      </div>
      {bad && <div className="li-sub" style={{ color: 'var(--bad)', marginTop: 8 }}>{bad}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onSubmit({ name: name.trim(), plate: plate.trim(), connector })}>
          Add and issue idTag
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Sheet>
  );
}

/* --------------------------------------------------------------- disputes */

export function Disputes() {
  const [state, setState] = useState('all');
  const { data, isLoading, error } = useDisputes(state);
  if (isLoading) return <Loading />;
  if (error || !data) {
    return <Empty title="Dispute records are not available." hint={loadFailedHint(error)} />;
  }

  return (
    <>
      <div className="tiles">
        <Tile k="Disputes" v={data.length} d={state === 'all' ? 'all time' : state.replace(/_/g, ' ')} />
        <Tile k="Under review" v={data.filter((d) => d.state === 'under_review').length} d="need a decision" />
        <Tile k="Refunded" v={data.filter((d) => d.state === 'resolved_refunded').length} d="settled for the driver" />
        <Tile k="Rejected" v={data.filter((d) => d.state === 'resolved_rejected').length} d="claim not upheld" />
      </div>

      <div className="toolbar">
        <div className="chips">
          {['all', 'under_review', 'resolved_refunded', 'resolved_rejected'].map((s) => (
            <Chip key={s} selected={state === s} onClick={() => setState(s)}
                  label={s === 'all' ? 'All' : s.replace(/_/g, ' ')} />
          ))}
        </div>
      </div>

      {!data.length ? (
        <Empty
          title="No disputes raised."
          hint="A dispute is raised against a session from the driver app, and needs a note of at least ten characters — one with no explanation cannot be investigated."
        />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Raised</th><th>Driver</th><th>Session</th><th>Reason</th>
                <th className="num">kWh</th><th className="num">Charged</th><th>State</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.id}>
                  <td className="mono">{new Date(d.createdAt).toLocaleDateString()}</td>
                  <td className="li-title">{d.driverName}</td>
                  <td className="li-sub">{d.stationId} · c{d.connectorId}</td>
                  <td>
                    {d.reason.replace(/_/g, ' ')}
                    <div className="li-sub">{d.note}</div>
                  </td>
                  <td className="num mono">{Number(d.energyKwh ?? 0).toFixed(2)}</td>
                  <td className="num mono">{minor(d.costMinor)}</td>
                  <td>
                    <Pill tone={d.state === 'under_review' ? 'mid' : d.state === 'resolved_refunded' ? 'on' : 'off'}>
                      {d.state.replace(/_/g, ' ')}
                    </Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- tariffs */

/**
 * Price per kWh, per charge point.
 *
 * The CSMS holds no price — it is commercial data — so this writes to the
 * platform's own override table. Stored in minor units, because money is never
 * a float.
 */
/** A price as typed: digits, at most two decimals. Anything finer cannot be
 *  stored in minor units without rounding someone's money. */
const PRICE_SHAPE = /^(\d+(\.\d{0,2})?|\.\d{1,2})$/;

export function Tariffs() {
  const { data, isLoading, error: loadError } = useChargePoints();
  const { data: config } = useConfig();
  const { update } = useChargePointMutations();
  const [editing, setEditing] = useState<ChargePoint | null>(null);
  const [price, setPrice] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!data) return <Empty title="Tariffs are not available." hint={loadFailedHint(loadError)} />;

  /* Null, not a placeholder. This used to fall back to '—', and that dash was
     then sent to the server as the currency of a real tariff. */
  const defaultCurrency = config?.find((c) => c.key === 'money.currency')?.value?.trim() || null;
  const priced = data.items.filter((s) => s.price > 0);

  /* What is wrong with the dialog as it stands, or null when it can be saved.
     An empty box used to save as 0 — Number('') is 0 — which made a charge
     point free to charge on without anybody choosing that. A price is now
     required and must be more than zero; clearing a tariff is done on the
     charge point's Site & tariff dialog, where emptying the field means "no
     price" and says so. */
  const currency = editing ? (editing.currency ?? defaultCurrency) : null;
  const typed = price.trim();
  const problem = !editing ? null
    : !typed ? 'Enter a price per kWh.'
      : !PRICE_SHAPE.test(typed) ? 'A price is a number with at most two decimals, like 12.50.'
        : !(Number(typed) > 0) ? 'A price must be more than zero. To stop billing on this charge point, block it or clear its price under Charge points → Site & tariff.'
          : !currency ? 'There is no currency to price this in: the charge point has none and no default currency is set. Set the default under Maps & keys, or give the charge point a currency under Charge points → Site & tariff.'
            : null;

  const commit = () => {
    if (!editing || problem || !currency) return;
    setMsg(null);
    update.mutate(
      { id: editing.id, priceMinor: Math.round(Number(typed) * 100), currency },
      { onSuccess: () => { setEditing(null); setMsg(null); }, onError: (e: Error) => setMsg(errorText(e)) },
    );
  };

  return (
    <>
      <div className="tiles">
        <Tile k="Charge points" v={data.items.length} d="on the network" />
        <Tile k="Priced" v={priced.length} d={priced.length ? 'have a tariff' : 'none yet'} />
        <Tile k="Default currency" v={defaultCurrency ?? '—'} d={defaultCurrency ? 'set in Maps & keys' : 'not set — Maps & keys'} />
        <Tile
          k="Average"
          v={priced.length ? (priced.reduce((n, s) => n + s.price, 0) / priced.length).toFixed(2) : '—'}
          d="per kWh, where set"
        />
      </div>

      <div className="tbl" style={{ marginTop: 12 }}>
        <table>
          <thead>
            <tr>
              <th>Charge point</th><th>Connectors</th>
              <th className="num">Price / kWh</th><th>Currency</th><th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>
                  {s.connectors.length
                    ? [...new Set(s.connectors.map((c) => c.type))].join(' · ')
                    : <span className="faint">none reported</span>}
                </td>
                <td className="num mono">
                  {s.price > 0 ? s.price.toFixed(2) : <span className="faint">not set</span>}
                </td>
                <td className="mono">{s.currency ?? <span className="faint">—</span>}</td>
                <td className="act">
                  <button className="btn sm ghost" onClick={() => {
                    setEditing(s); setPrice(s.price ? String(s.price) : ''); setMsg(null);
                  }}>
                    {s.price > 0 ? 'Change' : 'Set price'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          role="dialog" aria-label="Set tariff"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
            display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 380 }}>
            <div className="li-title" style={{ fontSize: 15 }}>{editing.name}</div>
            <div className="li-sub" style={{ marginBottom: 10 }}>
              Price per kWh{currency ? ` in ${currency}` : ''}. A session
              already running keeps the tariff it started on.
            </div>
            <input className="field mono" value={price} inputMode="decimal" autoFocus
                   placeholder="12.50"
                   onChange={(e) => { setPrice(e.target.value); setMsg(null); }}
                   onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
                   aria-label="Price per kWh" />
            {(problem || msg) && (
              <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 6 }}>
                {problem ?? msg}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" style={{ flex: 1 }} onClick={commit}
                      disabled={!!problem || update.isPending}>
                {update.isPending ? 'Saving...' : 'Save'}
              </button>
              <button className="btn ghost" style={{ flex: 1 }} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="note" style={{ marginTop: 12 }}>
        <b>Price is commercial data, so the CSMS does not hold it.</b> These rows
        are the real charge points; the tariff is stored alongside them in the
        platform database, in minor units, and every change is recorded in the
        activity log.
      </div>
    </>
  );
}


/** Register a card that already exists, by the number printed on it. */
function AddCard({
  pending, onClose, onSubmit,
}: { pending: boolean; onClose: () => void; onSubmit: (tag: string) => void }) {
  const [tag, setTag] = useState('');
  return (
    <div className="drawer">
      <div className="drawer-body">
        <h3>Add an RFID card</h3>
        <label className="lbl">Card number</label>
        <input
          className="inp mono"
          autoFocus
          value={tag}
          placeholder="04A2B3C4D5E6"
          onChange={(e) => setTag(e.target.value.trim())}
        />
        <div className="li-sub" style={{ marginTop: 4 }}>
          Exactly as printed on the card. This is what the charger reads, so a
          typo is a card that is refused at the bay with nothing to explain it.
          It cannot be changed afterwards — a wrong one is revoked and replaced.
        </div>
        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="btn"
            disabled={pending || tag.length < 4}
            onClick={() => onSubmit(tag)}
          >
            {pending ? 'Adding…' : 'Add the card'}
          </button>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Revoke a lost card.
 *
 * Confirmed rather than one click, and said plainly, because it cannot be
 * undone: a card that can be switched back on is a card whoever found it can
 * still charge with.
 */
function ReportLost({
  tag, pending, onClose, onConfirm,
}: {
  tag: string;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="drawer">
      <div className="drawer-body">
        <h3>Report this card lost?</h3>
        <div className="li-sub mono" style={{ marginTop: 4 }}>{tag}</div>
        <div className="note" style={{ marginTop: 10 }}>
          The charger stops accepting it immediately, and this cannot be undone.
          The driver keeps charging from the app — only the card stops. Give
          them a replacement by adding its number.
        </div>
        <label className="lbl" style={{ marginTop: 10 }}>Why (optional)</label>
        <input
          className="inp"
          value={reason}
          placeholder="Lost, stolen, left the company"
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn" disabled={pending} onClick={() => onConfirm(reason.trim() || undefined)}>
            {pending ? 'Revoking…' : 'Revoke the card'}
          </button>
          <button className="btn ghost" onClick={onClose}>Keep it working</button>
        </div>
      </div>
    </div>
  );
}
